import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";

export default function PolitiqueConfidentialite() {
    const lastUpdate = "28 septembre 2026";

    const sections = [
        {
            id: "donnees-collectees",
            title: "1. Données collectées",
            content: [
                "Nom, prénom ou pseudonyme",
                "Adresse e-mail et numéro de téléphone",
                "Informations de profil (photo, biographie, préférences)",
                "Données liées aux événements créés ou consultés (titres, descriptions, interactions)",
                "Données de navigation (cookies, outils d’analyse type Google Analytics)",
                "Données de réservation : nom du détenteur du billet, date de naissance, nombre de places, tarif choisi, référence du billet",
                "Données de transaction : montant, date, statut du paiement et identifiant Stripe (jamais le numéro de carte)",
                "Pour les organisateurs vendant des billets : identifiant de leur compte de paiement Stripe, statut de vérification, nom de la banque et 4 derniers chiffres du compte (jamais l'IBAN complet)",
            ],
        },
        {
            id: "finalites",
            title: "2. Finalités de la collecte",
            content: [
                "Création et gestion de comptes utilisateurs",
                "Publication et gestion d’événements",
                "Mise en relation entre créateurs et participants",
                "Amélioration continue du site et de l’expérience utilisateur",
                "Envoi d’informations importantes liées au compte ou aux événements (notifications, rappels)",
                "Gestion des abonnements payants pour les créateurs (facturation, support, suivi)",
                "Vente de billets, émission et contrôle des billets (QR code) et gestion des transactions sécurisées",
                "Reversement aux organisateurs des sommes qui leur reviennent, via Stripe Connect",
                "Prévention de la fraude et respect des obligations de lutte contre le blanchiment (réalisées par Stripe)",
                "Envoi, si accepté, d’offres et actualités",
            ],
        },
        {
            id: "base-legale",
            title: "3. Base légale du traitement",
            content: [
                "Exécution du contrat (utilisation de la plateforme, abonnements, achats de billets)",
                "Consentement de l’utilisateur (cookies, newsletters, communications)",
                "Respect des obligations légales (comptabilité, facturation, fiscalité)",
                "Intérêt légitime pour la sécurité, la prévention de fraude et l’amélioration du service",
            ],
        },
        {
            id: "duree-conservation",
            title: "4. Durée de conservation",
            content: [
                "Comptes utilisateurs : jusqu’à suppression volontaire par l’utilisateur",
                "Données d’événements : conservées pendant la durée de l’événement + 2 ans max",
                "Données de paiement et facturation : 10 ans (obligation légale française)",
                "Cookies : 13 mois maximum",
            ],
        },
        {
            id: "partage",
            title: "5. Partage des données",
            content: [
                "Les données ne sont jamais revendues.",
                "Elles peuvent être partagées uniquement avec :",
                "• Les prestataires techniques (hébergeur, outils d’emailing sécurisés)",
                "• Stripe Payments Europe Ltd, notre prestataire de paiement agréé, pour le traitement des paiements et, via Stripe Connect, la gestion des comptes de paiement des organisateurs",
                "• L'organisateur de l'événement pour lequel vous achetez un billet (nom du détenteur, nombre de places, tarif), afin de gérer l'accès à son événement",
                "Pour la vérification d'identité des organisateurs (KYC) et la lutte contre la fraude, Stripe agit en tant que responsable de traitement indépendant. Voir sa politique : https://stripe.com/fr/privacy",
                "• Les administrations fiscales et autorités compétentes si la loi l’exige",
            ],
        },
        {
            id: "securite",
            title: "6. Sécurité des données",
            content:
                "Thrundrz met en place des mesures techniques et organisationnelles strictes pour protéger vos données (chiffrement, hébergement sécurisé, accès limité, mots de passe protégés). Les paiements sont traités exclusivement par Stripe, prestataire agréé conforme à la norme PCI-DSS : aucune donnée de carte bancaire ni aucun IBAN complet ne transite par nos serveurs.",
        },
        {
            id: "droits",
            title: "7. Vos droits (RGPD)",
            content: [
                "Droit d’accès, de rectification et de suppression",
                "Droit à la limitation et à l’opposition",
                "Droit à la portabilité des données",
                "Droit de retirer votre consentement à tout moment (cookies, newsletters)",
                "Pour exercer vos droits : contact@thrundrz.fr",
                "En cas de litige : www.cnil.fr",
            ],
        },
        {
            id: "cookies",
            title: "8. Cookies",
            content:
                "Le site utilise des cookies pour assurer son bon fonctionnement, améliorer la navigation et établir des statistiques. Vous pouvez accepter ou refuser les cookies non essentiels via le bandeau affiché lors de votre première visite.",
        },
    ];

    return (
        <>
            <Helmet>
                <title>Politique de confidentialité — Thrundrz</title>
                <meta name="description" content="Découvrez notre politique de confidentialité et comment nous protégeons vos données personnelles." />
                <link rel="canonical" href="https://thrundrz.fr/politique-confidentialite" />
            </Helmet>
            <Navbar />
            <main className="min-h-screen bg-black text-white font-Poppins lg:pt-20 pt-10">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12">
                    {/* En-tête */}
                    <header className="mb-12 text-start">
                        <div className="flex items-center gap-3 mb-4">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="36"
                                height="36"
                                fill="currentColor"
                                className="text-color6"
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 0a5 5 0 0 0-5 5v3a3 3 0 0 0-1 2.236V12a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1v-1.764A3 3 0 0 0 13 8V5a5 5 0 0 0-5-5zM4 5a4 4 0 0 1 8 0v3H4V5z" />
                            </svg>
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-color6">
                                    Politique de confidentialité (RGPD)
                                </h1>
                            </div>
                        </div>
                        <p className="text-sm sm:text-base text-gray-400 font-thin">
                            Dernière mise à jour : {lastUpdate}
                        </p>
                    </header>

                    {/* Sommaire */}
                    <nav
                        aria-label="Sommaire"
                        className="mb-10 rounded-2xl border border-color6 p-6 shadow-sm font-Poppins bg-[#111]"
                    >
                        <h2 className="text-lg font-semibold text-color6 mb-4">Sommaire</h2>
                        <ul className="grid gap-3 sm:grid-cols-2">
                            {sections.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-sm sm:text-base text-gray-300 hover:text-color6 hover:underline"
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Contenu */}
                    <div className="space-y-12">
                        {sections.map((s) => (
                            <section key={s.id} id={s.id}>
                                <h3 className="text-2xl sm:text-3xl font-bold text-color6 mb-4 text-start">
                                    {s.title}
                                </h3>

                                {Array.isArray(s.content) ? (
                                    <ul className="list-disc pl-6 space-y-2 text-gray-200 text-base sm:text-lg">
                                        {s.content.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-200 text-base sm:text-lg leading-relaxed text-justify">
                                        {s.content}
                                    </p>
                                )}
                            </section>
                        ))}
                    </div>

                    {/* Footer */}
                    <footer className="text-center text-gray-500 text-sm mt-16">
                        © {new Date().getFullYear()} Thrundrz — Tous droits réservés.
                    </footer>
                </div>
            </main>
            <Footer />
        </>
    );
}